import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonProps,
  Chip,
  ChipProps,
  Container,
  Divider,
  Grid,
  GridProps,
  IconProps,
  Stack,
  StackProps,
  SxProps,
  Typography,
  TypographyProps,
} from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';

import { useLocation } from 'react-router-dom';
import { TP_COLORS } from '../../../constants';
import {
  checkDiscountEligibility,
  checkUserPermission,
  getCountry,
} from '../../../helper';
import { useAppSelector } from '../../../hooks';
import { getCurrencySign } from '../Diagnostic';

import { getAuthState } from '../../../store/AuthSlice';
import {
  createPremiumSession,
  createPremiumSessionWithDetails,
  getPremiumProductPrice,
  getStripeState,
} from '../../../store/StripeSlice';

import HomeFooter from '../../Home/components/HomeFooter';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import HomeHeader from '../../Home/components/HomeHeader';
import LeadGenModal, { ILeadGenState } from './modals/LeadGenModal';
import DiscountBanner from './components/DiscountBanner';
import { USER_PERMISSION_INDIVIDUALS } from '../../../store/V2/RolesSlice';
import { getTenantsState } from '../../../store/V2/TenantsSlice';

const constructBreadcrumbOptions = () => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
    {
      type: 'text',
      title: 'Upgrade',
      highlight: true,
    },
  ];

  return options;
};

const containerStyles: SxProps = {
  backgroundColor: '#E5F7F666',
  color: '#1E293B',
  minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
};

const topStackProps: StackProps['sx'] = {
  direction: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  style: { padding: '30px 0' },
};

const buttonProps: ButtonProps = {
  fullWidth: true,
  size: 'large',
  style: {
    fontWeight: 600,
  },
  variant: 'contained',
};

const titleBoxStyles: SxProps = {
  background: {
    xs: `url('${process.env.PUBLIC_URL}/images/planPage/green-shape.png') top right/75% no-repeat`,
    sm: `url('${process.env.PUBLIC_URL}/images/planPage/green-shape.png') top right/contain no-repeat`,
  },
};

const titleTypoProps: TypographyProps = {
  variant: 'h4',
  fontWeight: 800,
  marginBottom: '20px',
  sx: {
    fontSize: { xs: '26px', sm: '32px', md: '40px' },
    background: `linear-gradient(90deg, #01B8AF 0%, #00615D 100%)`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

const titleHelperTypoProps: TypographyProps = {
  variant: 'h6',
  fontWeight: '600',
  sx: { fontSize: { xs: '16px', sm: '18px', md: '20px' } },
};

const subTitleTypoProps: TypographyProps = {
  variant: 'h5',
  fontSize: '30px',
  fontWeight: '500',
  textAlign: 'center',
  sx: { color: TP_COLORS.blue.dark },
};

const subTitleColorTypoProps: TypographyProps = {
  variant: 'h5',
  fontSize: '30px',
  fontWeight: '600',
  sx: {
    background: 'linear-gradient(91deg, #DB86F8 40.69%, #006FD6 71.6%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

const plansContainerStyles = {
  maxWidth: '1060px !important',
  padding: '40px 0 !important',
};

const planStackProps: StackProps = {
  padding: '25px',
  alignItems: 'center',
  minHeight: '300px',
  justifyContent: 'space-between',
  sx: { borderRadius: '20px', border: '1px solid #CBD5E1' },
};

const planTypoProps: TypographyProps = {
  variant: 'h6',
  fontWeight: '600',
  sx: { color: TP_COLORS.gray.dark },
  marginBottom: '25px',
};

// const priceTypoProps: TypographyProps = {
//   fontSize: '30px',
//   fontWeight: '600',
//   sx: { color: TP_COLORS.blue.dark },
// };

const planGridProps: GridProps = {
  xs: 12,
  sm: 6,
  md: 4,
  lg: 3,
  display: 'flex',
  sx: { padding: { xs: '13px 5px 13px 13px', md: '13px 5px' } },
};

// const discountBoxProps: BoxProps = {
//   display: 'flex',
//   alignItems: 'center',
// };

// const discountTypoProps: TypographyProps = {
//   variant: 'subtitle2',
//   sx: {
//     textDecoration: 'line-through',
//     color: TP_COLORS.blue.dark,
//   },
// };

const highlightTypoProps: TypographyProps = {
  variant: 'h5',
  fontSize: '18px',
  sx: {
    background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

const checkIconProps: IconProps['sx'] = {
  style: {
    color: '#DB86F8',
    marginRight: '8px',
  },
};

const chipProps: ChipProps = {
  size: 'small',
  sx: {
    fontWeight: '600',
    borderRadius: '4px',
    backgroundColor: TP_COLORS.yellow.light,
    color: TP_COLORS.gray.darken,
    marginLeft: '8px',
  },
};

const planHighlightsGridProps: GridProps = {
  padding: '15px 20px',
  margin: '40px 0',
  sx: {
    borderRadius: '20px',
    border: '2px solid #DB86F8',
    background:
      'linear-gradient(90deg, rgba(219, 134, 248, 0.15) 0%, rgba(0, 111, 214, 0.15) 100%)',
  },
};

const featuresStackProps: StackProps = {
  alignItems: 'center',
  padding: '20px',
  margin: '10px',
  textAlign: 'center',
  sx: {
    borderRadius: '20px',
    backgroundColor: '#F1F5F9',
  },
};

const planHighlights = [
  '200+ Subject Videos',
  '50000+ Practice Questions',
  'AI Assistance',
  'AI Detailed Marking Scheme',
  'Progress Tracker',
  'Topic Planner',
  'Paper wise Quizzes',
  'Assessments',
];

const featuresList = [
  {
    img: `${process.env.PUBLIC_URL}/images/planPage/bullseye.png`,
    title: 'Tailor-made lessons',
    description:
      'Tutopiya creates a personalised learning plan for each student based on their strengths and weaknesses.',
  },
  {
    img: `${process.env.PUBLIC_URL}/images/planPage/books.png`,
    title: 'Tutopiya’s Learning Platform',
    description:
      'Explore a comprehensive library of resources, including extensive notes, quizzes and past papers, all curated according to topics and subtopics.',
  },
  {
    img: `${process.env.PUBLIC_URL}/images/planPage/globe.png`,
    title: 'Connect with Global Learners',
    description:
      'This unique opportunity fosters cultural exchange, widens your perspective, and prepares you for a truly globalised world.',
  },
  {
    img: `${process.env.PUBLIC_URL}/images/planPage/robot.png`,
    title: 'Integrated AI',
    description:
      "Don't just get told you're wrong, understand why! Tutopiya's AI tutor will explain incorrect answers and guide you towards the correct approach.",
  },
  {
    img: `${process.env.PUBLIC_URL}/images/planPage/rocket.png`,
    title: 'Boost Your Confidence',
    description:
      'Get detailed reports that not only highlight your progress, but also recommend specific study hours for each topic, ensuring you focus on what matters most.',
  },
];

const PlanPage: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  // State
  const [isLeadGenModalOpen, setIsLeadGenModalOpen] = useState<boolean>(false);
  const [selectedPriceId, setSelectedPriceId] = useState<string>('');

  // Store
  const authState = useAppSelector(getAuthState);
  const stripeState = useAppSelector(getStripeState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;
  const { premiumPricesList, status } = stripeState;

  const discountBannerPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      headerContents: [
        USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_DISCOUNT_BANNER,
      ],
    },
  });

  const monthlyPrice = premiumPricesList.length
    ? premiumPricesList[0]
    : undefined;

  // const quarterlyPrice = premiumPricesList.length
  //   ? premiumPricesList[1]
  //   : undefined;

  const yearlyPrice = premiumPricesList.length
    ? premiumPricesList[2]
    : undefined;

  const yearlyTotal = (monthlyPrice?.unitAmount || 1) * 12;
  const yearlyOffRate =
    Math.round(
      (((yearlyTotal - (yearlyPrice?.unitAmount || 1)) / yearlyTotal) * 100) / 5
    ) * 5;

  // const quarterlyTotal = (monthlyPrice?.unitAmount || 1) * 3;
  // const quarterlyOffRate =
  //   Math.round(
  //     (((quarterlyTotal - (quarterlyPrice?.unitAmount || 1)) / quarterlyTotal) *
  //       100) /
  //       5
  //   ) * 5;

  const currency = getCurrencySign(monthlyPrice?.currency || '');
  const breadcrumbOptions = constructBreadcrumbOptions();
  const returnUrl = location.pathname.replace('learning-portal/', '');
  const isDiscountEligible = checkDiscountEligibility();

  useEffect(() => {
    if (authUser) {
      dispatch(
        getPremiumProductPrice({ userId: authUser._id, country: getCountry() })
      );
    }
  }, []);

  const handleSubscribe = (priceId: string) => {
    if (
      authUser &&
      (authUser.role.name === 'Student' || authUser.isLeadGenSubmitted)
    ) {
      dispatch(
        createPremiumSession({
          userId: authUser._id,
          priceId,
          returnUrl,
        })
      );
    } else {
      setIsLeadGenModalOpen(true);
      setSelectedPriceId(priceId);
    }
  };

  const handleLeadGenSubmit = (values: ILeadGenState) => {
    if (values && authUser) {
      dispatch(
        createPremiumSessionWithDetails({
          ...values,
          userId: authUser._id,
          priceId: selectedPriceId,
          returnUrl,
        })
      );
    }
  };

  return (
    <Box sx={containerStyles}>
      <HomeHeader />

      {((authUser && discountBannerPermission) || !authUser) &&
      isDiscountEligible ? (
        <DiscountBanner />
      ) : null}

      <Container maxWidth="lg">
        <Stack {...topStackProps}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor={TP_COLORS.gray.light}
          />
        </Stack>

        <Stack alignItems="center" marginBottom="20px" textAlign="center">
          <Box sx={titleBoxStyles}>
            <Typography {...titleTypoProps}>
              Unlock Your Full Learning Potential !
            </Typography>
          </Box>

          <Typography {...titleHelperTypoProps}>
            Upgrade to premium for exclusive features and personalized support
          </Typography>
        </Stack>

        {/* Plans Container */}
        <Container sx={plansContainerStyles}>
          {status === 'loading' && premiumPricesList.length === 0 ? (
            <div style={{ padding: '30px' }}>
              <BasicLoader />
            </div>
          ) : (
            <Grid container spacing={2} justifyContent="center">
              {/* Yearly */}
              {yearlyPrice && (
                <Grid item xs={12} sm={6} md={4}>
                  <Stack {...planStackProps}>
                    <Stack width="100%" alignItems="center">
                      <Typography {...planTypoProps}>Yearly</Typography>
                      <Divider flexItem sx={{ borderStyle: 'dashed' }} />
                    </Stack>

                    <Stack alignItems="center" spacing={2}>
                      <Box display="flex" alignItems="center">
                        <Typography fontSize="15px" fontWeight="600">
                          12 months
                        </Typography>
                        <Chip
                          {...chipProps}
                          label={`Save${yearlyOffRate}% off`}
                        />
                      </Box>
                      <Box display="flex" alignItems="flex-end">
                        <Typography
                          fontSize="23px"
                          fontWeight="600"
                          lineHeight="26px"
                        >
                          {currency}
                        </Typography>
                        <Typography
                          fontSize="30px"
                          fontWeight="600"
                          lineHeight="30px"
                          marginRight="3px"
                        >
                          {(yearlyPrice.unitAmount / 1200).toFixed()}
                        </Typography>
                        <Typography
                          fontSize="16px"
                          fontWeight="600"
                          color={TP_COLORS.tutopiyaGreenMain.darken}
                        >
                          /Month
                        </Typography>
                      </Box>
                      <Typography
                        fontSize="14px"
                        color={TP_COLORS.tutopiyaGreenMain.darken}
                      >
                        Billed at {currency}
                        {yearlyPrice.unitAmount / 100}
                        /year
                      </Typography>
                    </Stack>

                    <Button
                      {...buttonProps}
                      color="primary"
                      onClick={() => handleSubscribe(yearlyPrice.id || '')}
                    >
                      Subscribe
                    </Button>
                  </Stack>
                </Grid>
              )}

              {/* Quarterly */}
              {/* {quarterlyPrice && (
                <Grid item xs={12} sm={6} md={4}>
                  <Stack {...planStackProps}>
                    <Stack width="100%" alignItems="center">
                      <Typography {...planTypoProps}>Quarterly</Typography>
                      <Divider flexItem sx={{ borderStyle: 'dashed' }} />
                    </Stack>

                    <Stack alignItems="center" spacing={2}>
                      <Box display="flex" alignItems="center">
                        <Typography fontSize="15px" fontWeight="600">
                          3 months
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="flex-end">
                        <Typography
                          fontSize="23px"
                          fontWeight="600"
                          lineHeight="26px"
                        >
                          {currency}
                        </Typography>
                        <Typography
                          fontSize="30px"
                          fontWeight="600"
                          lineHeight="30px"
                          marginRight="3px"
                        >
                          {(quarterlyPrice.unitAmount / 300).toFixed()}
                        </Typography>
                        <Typography
                          fontSize="16px"
                          fontWeight="600"
                          color={TP_COLORS.tutopiyaGreenMain.darken}
                        >
                          /Month
                        </Typography>
                      </Box>
                      <Typography
                        fontSize="14px"
                        color={TP_COLORS.tutopiyaGreenMain.darken}
                      >
                        Billed at {currency}
                        {quarterlyPrice.unitAmount / 100}
                        /Quarter
                      </Typography>
                    </Stack>

                    <Button
                      {...buttonProps}
                      color="inherit"
                      onClick={() => handleSubscribe(quarterlyPrice.id || '')}
                    >
                      Subscribe
                    </Button>
                  </Stack>
                </Grid>
              )} */}

              {/* Monthly */}
              {monthlyPrice && (
                <Grid item xs={12} sm={6} md={4}>
                  <Stack {...planStackProps}>
                    <Stack width="100%" alignItems="center">
                      <Typography {...planTypoProps}>Monthly</Typography>
                      <Divider flexItem sx={{ borderStyle: 'dashed' }} />
                    </Stack>

                    <Stack alignItems="center" spacing={2}>
                      <Box display="flex" alignItems="center">
                        <Typography fontSize="15px" fontWeight="600">
                          1 month
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="flex-end">
                        <Typography
                          fontSize="23px"
                          fontWeight="600"
                          lineHeight="26px"
                        >
                          {currency}
                        </Typography>
                        <Typography
                          fontSize="30px"
                          fontWeight="600"
                          lineHeight="30px"
                          marginRight="3px"
                        >
                          {(monthlyPrice.unitAmount / 100).toFixed()}
                        </Typography>
                        <Typography
                          fontSize="16px"
                          fontWeight="600"
                          color={TP_COLORS.tutopiyaGreenMain.darken}
                        >
                          /Month
                        </Typography>
                      </Box>
                      <Typography
                        fontSize="14px"
                        color={TP_COLORS.tutopiyaGreenMain.darken}
                      >
                        Billed at {currency}
                        {monthlyPrice.unitAmount / 100} a month
                      </Typography>
                    </Stack>

                    <Button
                      {...buttonProps}
                      color="inherit"
                      onClick={() => handleSubscribe(monthlyPrice.id || '')}
                    >
                      Subscribe
                    </Button>
                  </Stack>
                </Grid>
              )}
            </Grid>
          )}
        </Container>

        <Stack alignItems="center" sx={{ margin: { xs: '0px', md: '20px 0' } }}>
          <Typography {...subTitleTypoProps}>
            Ready to unlock your academic potential
          </Typography>
          <Box display="flex">
            <Typography {...subTitleTypoProps}>with&nbsp;</Typography>
            <Typography {...subTitleColorTypoProps}>Tutopiya ?</Typography>
          </Box>
        </Stack>

        <Grid container {...planHighlightsGridProps}>
          {planHighlights.map((element, index) => (
            <Grid item key={index} {...planGridProps}>
              <CheckIcon {...checkIconProps} />
              <Typography {...highlightTypoProps}>{element}</Typography>
            </Grid>
          ))}
        </Grid>

        <Grid container margin="40px 0" justifyContent="center">
          {featuresList.map((element, index) => (
            <Grid item key={index} xs={12} sm={6} md={4} display="flex">
              <Stack {...featuresStackProps}>
                <img
                  src={element.img}
                  alt="bullseye-logo"
                  style={{ width: '48px', height: '48px' }}
                />
                <Typography fontSize="18px" fontWeight="600" margin="10px 0">
                  {element.title}
                </Typography>
                <Typography variant="subtitle2">
                  {element.description}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>

      <HomeFooter />
      {/* Modals */}
      {isLeadGenModalOpen && (
        <LeadGenModal
          open={isLeadGenModalOpen}
          onClose={() => setIsLeadGenModalOpen(false)}
          onConfirm={handleLeadGenSubmit}
        />
      )}
    </Box>
  );
};

export default PlanPage;
